import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React, { useState } from "react";
import { Show } from "../components";

type UseConfirmDialogProps = {
  cancelButtonText?: string;
  confirmButtonText?: string;
  hideButtons?: { cancel?: boolean; confirm?: boolean };
  text?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  title?: string;
};

/**
 * Custom React hook to manage the state and rendering of a confirmation dialog.
 *
 * @param {Object} params - The parameters for configuring the confirmation dialog.
 * @param {string} [params.cancelButtonText] - Custom text for the cancel button.
 * @param {string} [params.confirmButtonText] - Custom text for the confirm button.
 * @param {Object} [params.hideButtons] - Object to control visibility of cancel and confirm buttons.
 * @param {boolean} [params.hideButtons.cancel] - Whether to hide the cancel button.
 * @param {boolean} [params.hideButtons.confirm] - Whether to hide the confirm button.
 * @param {string} [params.text] - The content text to display in the dialog.
 * @param {() => void} [params.onCancel] - Callback invoked when the cancel action is triggered.
 * @param {() => void} [params.onConfirm] - Callback invoked when the confirm action is triggered.
 * @param {string} [params.title] - Custom title for the dialog.
 *
 * @returns An object containing:
 *   - `render`: A function that returns the dialog JSX element.
 *   - `setVisible`: Function to control the dialog's visibility.
 *   - `setText`: Function to update the dialog's content text.
 *
 * @example
 * const { render: Dialog, setVisible } = useConfirmDialog({ text: "Are you sure?" });
 * // To show the dialog:
 * setVisible(true);
 * // In your component JSX:
 * <Dialog />
 */
export function useConfirmDialog({
  cancelButtonText,
  confirmButtonText,
  hideButtons,
  text,
  onCancel = noop,
  onConfirm = noop,
  title,
}: UseConfirmDialogProps) {
  const { t } = useTranslations();

  const [visible, setVisible] = useState<boolean>(false);
  const [textModal, setTextModal] = useState<string>(text ?? "");

  const _title = title != null ? title : t("common:alert");
  const _cancelButtonText =
    cancelButtonText != null ? cancelButtonText : t("common:actions.cancel");
  const _confirmButtonText =
    confirmButtonText != null
      ? confirmButtonText
      : t("common:actions.yes-confirm");

  const _handleConfirm = () => {
    setVisible(false);
    onConfirm();
  };

  const _handleCancel = () => {
    setVisible(false);
    onCancel();
  };

  const render = () => (
    <Show when={visible}>
      <ConfirmDialog
        cancelButtonText={_cancelButtonText}
        confirmButtonText={_confirmButtonText}
        contentText={textModal}
        hideButtons={{
          cancel: hideButtons?.cancel ?? false,
          confirm: hideButtons?.confirm ?? false,
        }}
        onCancel={_handleCancel}
        onConfirm={_handleConfirm}
        title={_title}
        visible={visible}
      />
    </Show>
  );

  return {
    render,
    setVisible,
    setText: setTextModal,
  };
}

type ConfirmDialogProps = {
  cancelButtonText: string;
  contentText: string;
  confirmButtonText: string;
  hideButtons: { cancel: boolean; confirm: boolean };
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  visible: boolean;
};

const ConfirmDialog = ({
  contentText,
  cancelButtonText,
  confirmButtonText,
  hideButtons,
  onCancel,
  onConfirm,
  title,
  visible,
}: ConfirmDialogProps) => {
  return (
    <Dialog
      aria-describedby="confirm-dialog-description"
      aria-labelledby="confirm-dialog-title"
      data-testid="confirm-dialog"
      id="confirm-dialog"
      onClose={onConfirm}
      open={visible}
      PaperProps={{ style: { padding: "8px 0 24px 0" } }}
    >
      <DialogTitle data-testid="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent data-testid="confirm-dialog-description">
        <DialogContentText>{contentText}</DialogContentText>
      </DialogContent>

      <DialogActions
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "12px",
          padding: "0 20px",
        }}
      >
        <Show when={!hideButtons.cancel}>
          <Button
            autoFocus
            color="primary"
            data-testid="confirm-dialog-cancel-btn"
            onClick={onCancel}
            style={{ textTransform: "uppercase" }}
          >
            {cancelButtonText}
          </Button>
        </Show>
        <Show when={!hideButtons.confirm}>
          <Button
            color="primary"
            data-testid="confirm-dialog-confirm-btn"
            onClick={onConfirm}
            style={{ textTransform: "uppercase" }}
            variant="contained"
          >
            {confirmButtonText}
          </Button>
        </Show>
      </DialogActions>
    </Dialog>
  );
};

const noop = () => {
  return;
};
